type Cursor = {
  userId: string;
  seat: number;
  color: string;
  username?: string;
  x: number;
  y: number;
  tx: number;
  ty: number;
  lastUpdate: number;
};

export type CursorUpdate = {
  userId: string;
  seat: number;
  color: string;
  x: number;
  y: number;
  username?: string;
};

export type CursorPresence = {
  userId: string;
  username: string;
  seat: number;
  color: string;
};

export class CursorOverlay {
  private cursors = new Map<string, Cursor>();
  private ctx: CanvasRenderingContext2D;
  private rafId = 0;
  private destroyed = false;

  constructor(private canvas: HTMLCanvasElement, private getSize: () => {w: number; h: number}) {
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error('CursorOverlay: 2D context unavailable');
    }
    this.ctx = ctx;
    this.tick = this.tick.bind(this);
    this.rafId = requestAnimationFrame(this.tick);
  }

  upsert(update: CursorUpdate): void {
    const existing = this.cursors.get(update.userId);
    if (existing) {
      existing.tx = update.x;
      existing.ty = update.y;
      existing.color = update.color;
      existing.seat = update.seat;
      if (update.username) existing.username = update.username;
      existing.lastUpdate = performance.now();
      return;
    }
    this.cursors.set(update.userId, {
      userId: update.userId,
      seat: update.seat,
      color: update.color,
      username: update.username,
      x: update.x,
      y: update.y,
      tx: update.x,
      ty: update.y,
      lastUpdate: performance.now()
    });
  }

  setPresence(members: CursorPresence[], excludeUserId?: string): void {
    const ids = new Set(members.map(m => m.userId));
    for (const id of [...this.cursors.keys()]) {
      if (!ids.has(id)) this.cursors.delete(id);
    }
    for (const m of members) {
      if (excludeUserId && m.userId === excludeUserId) continue;
      const existing = this.cursors.get(m.userId);
      if (existing) {
        existing.username = m.username;
        existing.color = m.color;
        existing.seat = m.seat;
      }
    }
    if (excludeUserId) {
      this.cursors.delete(excludeUserId);
    }
  }

  remove(userId: string): void {
    this.cursors.delete(userId);
  }

  destroy(): void {
    this.destroyed = true;
    cancelAnimationFrame(this.rafId);
  }

  private tick(): void {
    if (this.destroyed) return;
    this.rafId = requestAnimationFrame(this.tick);

    const {w, h} = this.getSize();
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    const ctx = this.ctx;
    ctx.clearRect(0, 0, w, h);

    for (const c of this.cursors.values()) {
      c.x += (c.tx - c.x) * 0.25;
      c.y += (c.ty - c.y) * 0.25;
      drawCursor(ctx, c, w, h);
    }
  }
}

function drawCursor(ctx: CanvasRenderingContext2D, c: Cursor, w: number, h: number): void {
  const px = c.x * w;
  const py = c.y * h;
  ctx.save();
  ctx.translate(px, py);

  ctx.fillStyle = c.color;
  ctx.strokeStyle = '#0a0c12';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(20, 16);
  ctx.lineTo(9, 18);
  ctx.lineTo(14, 32);
  ctx.lineTo(7, 34);
  ctx.lineTo(2, 20);
  ctx.lineTo(-5, 24);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  const label = `P${c.seat + 1}${c.username ? ' · ' + c.username : ''}`;
  ctx.font = 'bold 14px Arial, sans-serif';
  const m = ctx.measureText(label);
  const padX = 8;
  const lw = m.width + padX * 2;
  const lh = 22;
  ctx.fillStyle = 'rgba(8, 12, 20, 0.82)';
  ctx.strokeStyle = c.color;
  ctx.lineWidth = 1.5;
  roundRect(ctx, 16, 30, lw, lh, 6);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = c.color;
  ctx.textBaseline = 'middle';
  ctx.fillText(label, 16 + padX, 30 + lh / 2);

  ctx.restore();
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
